import { Fragment, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { assistantPageLink, parseAssistantText, type AssistantInline, type AssistantLinkKey } from '@zerogap/shared';
import { arrow, msg, tr } from '../i18n';
import { useTenant } from '../session';

/**
 * What each button says: the portal's own name for the page, so it matches the sidebar. Typed against
 * the shared page list, so adding a page there without wording here fails to compile, and marked with
 * msg() so `check:i18n` insists on the Hebrew.
 */
const PAGE_LABELS: Record<AssistantLinkKey, string> = {
  overview: msg('Overview'),
  computers: msg('Computers'),
  'add-computer': msg('Connect a computer'),
  'add-phone': msg('Connect a phone'),
  plans: msg('Backup plans'),
  restore: msg('Get files back'),
  recoveries: msg('Recoveries'),
  alerts: msg('Alerts'),
  team: msg('Team'),
  activity: msg('Activity'),
  billing: msg('Subscription & usage'),
  settings: msg('Settings'),
  'shared-links': msg('Shared links'),
  computer: msg('Open this computer'),
};

/**
 * The assistant's own sentences: short paragraphs, lists, **bold**, `paths` and page buttons
 * (parseAssistantText in @zerogap/shared decides what is what; nothing it returns is markup).
 *
 * `[[page:team]]` becomes a button that navigates; the label is the portal's own name for that page, so
 * it matches the sidebar and is already translated. Things this does deliberately:
 *
 *  - a marker the model invented resolves to nothing and is dropped, so a wrong guess costs a word
 *    rather than showing a broken link;
 *  - the destination comes from the shared page list, never from the model, so it cannot send anybody
 *    to an address of its own devising;
 *  - a `path` is one left-to-right run, so a Windows path inside a Hebrew sentence stays readable;
 *  - a half-arrived marker is hidden while the answer is still streaming, so nobody watches `[[pag` be
 *    typed out.
 *
 * `plain` shows the text exactly as typed: what a person wrote is never reinterpreted.
 */
export function AssistantText({ text, streaming, plain }: { text: string; streaming?: boolean; plain?: boolean }) {
  const t = useTenant();
  const nav = useNavigate();

  if (plain)
    return (
      <div className="assist-text plain" dir="auto">
        {text}
      </div>
    );

  // While streaming, hide a trailing fragment that may yet turn out to be a marker.
  let body = text;
  if (streaming) {
    const open = body.lastIndexOf('[[');
    if (open !== -1 && !body.slice(open).includes(']]')) body = body.slice(0, open);
  }

  const inline = (parts: AssistantInline[], key: string): ReactNode[] =>
    parts.map((p, i) => {
      const k = `${key}.${i}`;
      switch (p.t) {
        case 'text':
          return <Fragment key={k}>{p.v}</Fragment>;
        case 'break':
          return <br key={k} />;
        case 'bold':
          return <strong key={k}>{inline(p.v, k)}</strong>;
        case 'code':
          return (
            <bdi key={k} dir="ltr" className="assist-code">
              {/* A long path wraps at a folder boundary rather than in the middle of a name. */}
              {p.v.split(/(?<=[\\/])/).map((part, j) => (
                <Fragment key={j}>
                  {j > 0 && <wbr />}
                  {part}
                </Fragment>
              ))}
            </bdi>
          );
        case 'page': {
          const link = assistantPageLink(p.key, p.id);
          if (!link) return null; // not a page: the marker disappears rather than misleading anybody
          return (
            <button key={k} type="button" className="assist-link" onClick={() => nav(t.base + link.path)}>
              {tr(PAGE_LABELS[link.key])}
              <span aria-hidden>{arrow.forward()}</span>
            </button>
          );
        }
      }
    });

  return (
    <div className="assist-text" dir="auto">
      {parseAssistantText(body).map((b, i) => {
        if (b.t === 'p') return <p key={i}>{inline(b.v, `p${i}`)}</p>;
        if (b.t === 'heading') return <p key={i} className="assist-heading">{inline(b.v, `h${i}`)}</p>;
        const List = b.t === 'ol' ? 'ol' : 'ul';
        return (
          <List key={i}>
            {b.items.map((item, j) => (
              <li key={j}>{inline(item, `l${i}.${j}`)}</li>
            ))}
          </List>
        );
      })}
    </div>
  );
}
